/**
 * Login page — wraps the form scaffold with react-hook-form + zod validation.
 *
 * BUG-022: zod schema mirrors the backend pydantic ``LoginRequest`` exactly
 * (username 3-64, password 1-128). HTML constraints (``required`` / ``minLength`` /
 * ``maxLength``) on every Input are paired with the same zod rule so the user is
 * blocked at INPUT time, not at submit. Submit button explicitly surfaces the
 * disabled reason via an inline hint.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { LanguageChips } from "@/components/brand/LanguageChips";
import { Wordmark } from "@/components/brand/Wordmark";
import { TOKENS } from "@/lib/tokens";

import { useLogin } from "./useLogin";

const schema = z.object({
  username: z
    .string()
    .min(3, "auth:login.fields.username")
    .max(64, "auth:login.fields.username"),
  password: z
    .string()
    .min(1, "auth:login.fields.password")
    .max(128, "auth:login.fields.password"),
});

type LoginValues = z.infer<typeof schema>;

export function LoginPage() {
  const { t } = useTranslation(["auth", "common"]);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: { username: "", password: "" },
  });
  const login = useLogin();

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        backgroundImage: `linear-gradient(135deg, ${TOKENS.depth} 0%, ${TOKENS.depthElevated} 100%)`,
      }}
    >
      <Card
        className="w-full max-w-md mx-auto"
        style={{
          backgroundColor: TOKENS.surfaceElevated,
          boxShadow: TOKENS.shadowStrong,
          border: `1px solid ${TOKENS.border}`,
          borderRadius: TOKENS.radius,
        }}
      >
        <CardHeader
          className="flex flex-row items-center justify-between"
          style={{
            minHeight: "56px",
            borderBottom: `2px solid ${TOKENS.primary}`,
          }}
        >
          <Wordmark size="md" />
          <LanguageChips />
        </CardHeader>
        <CardContent>
          <form
            noValidate
            onSubmit={handleSubmit((values) => login.mutate(values))}
            className="space-y-4"
            style={{ marginTop: "1rem" }}
          >
            <div>
              <Label htmlFor="username">
                {t("auth:login.fields.username")}
              </Label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                required
                minLength={3}
                maxLength={64}
                aria-invalid={errors.username ? "true" : "false"}
                aria-describedby={
                  errors.username ? "username-error" : undefined
                }
                {...register("username")}
              />
              {errors.username && (
                <p
                  id="username-error"
                  className="text-sm mt-1"
                  style={{ color: "#dc2626" }}
                >
                  {t("common:errors.invalid_input")}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="password">
                {t("auth:login.fields.password")}
              </Label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                required
                minLength={1}
                maxLength={128}
                aria-invalid={errors.password ? "true" : "false"}
                aria-describedby={
                  errors.password ? "password-error" : undefined
                }
                {...register("password")}
              />
              {errors.password && (
                <p
                  id="password-error"
                  className="text-sm mt-1"
                  style={{ color: "#dc2626" }}
                >
                  {t("common:errors.invalid_input")}
                </p>
              )}
            </div>
            {isDirty && !isValid ? (
              <p
                className="text-xs"
                role="status"
                style={{ color: TOKENS.textMuted }}
              >
                {t("auth:login.fix_errors_to_continue")}
              </p>
            ) : (
              <p
                className="text-xs"
                role="note"
                style={{ color: TOKENS.textMuted }}
              >
                {t("auth:login.subtitle")}
              </p>
            )}
            <Button
              type="submit"
              disabled={!isValid || isSubmitting || login.isPending}
              className="w-full"
              style={{
                backgroundColor: TOKENS.primary,
                color: TOKENS.primaryFg,
                minHeight: "44px",
                fontWeight: 600,
              }}
            >
              {login.isPending
                ? t("common:actions.loading")
                : t("auth:login.submit")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
